import React, { useState, useRef } from 'react';
import { Save, Paperclip, X } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { toast } from 'react-hot-toast';

interface NewCommentEditorProps {
  submissionId: string;
  onSave: () => void;
}

interface AttachedFile {
  file: File;
  preview?: string;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain'
];

export const NewCommentEditor: React.FC<NewCommentEditorProps> = ({ submissionId, onSave }) => {
  const [content, setContent] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    const currentSize = attachedFiles.reduce((acc, file) => acc + file.file.size, 0);

    if (totalSize + currentSize > MAX_FILE_SIZE) {
      toast.error('La taille totale des fichiers ne doit pas dépasser 20MB');
      return;
    }

    const invalidFiles = files.filter(file => !ALLOWED_FILE_TYPES.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error('Certains fichiers ne sont pas dans un format supporté');
      return;
    }

    const emptyFiles = files.filter(file => file.size === 0);
    if (emptyFiles.length > 0) {
      toast.error('Les fichiers vides ne sont pas autorisés');
      return;
    }

    const newFiles = files.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
    }));

    setAttachedFiles(prev => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => {
      const updated = [...prev];
      const [removed] = updated.splice(index, 1);
      if (removed.preview) URL.revokeObjectURL(removed.preview);
      return updated;
    });
  };

  const handleSave = async () => {
    if (!content.trim() && attachedFiles.length === 0) {
      toast.error('Le commentaire ne peut pas être vide');
      return;
    }

    const emptyFiles = attachedFiles.filter(({ file }) => file.size === 0);
    if (emptyFiles.length > 0) {
      toast.error('Les fichiers vides ne sont pas autorisés');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('submissionId', submissionId);
      formData.append('content', content);
      attachedFiles.forEach(({ file }) => formData.append('files', file));

      const response = await fetch('/api/add-comment', {
        method: 'POST',
        body: formData,
      });

      const responseText = await response.text();
      let result: any;

      try {
        result = JSON.parse(responseText);
      } catch {
        console.error('❌ Réponse non-JSON du serveur:', responseText);
        throw new Error('Réponse serveur invalide');
      }

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de l’ajout du commentaire');
      }

      setContent('');
      setAttachedFiles([]);
      toast.success('Commentaire ajouté avec succès');
      onSave();
    } catch (error) {
      console.error('❌ Erreur ajout commentaire:', error);
      toast.error('Erreur lors de l’ajout du commentaire');
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div className="space-y-4">
      <RichTextEditor content={content} onChange={setContent} />

      <div className="border-t pt-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded-md hover:bg-gray-50"
          >
            <Paperclip className="h-4 w-4 mr-2" />
            Joindre des fichiers
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept={ALLOWED_FILE_TYPES.join(',')}
            onChange={handleFileSelect}
          />
        </div>

        {attachedFiles.length > 0 && (
          <div className="mt-4 space-y-2">
            {attachedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  {file.preview ? (
                    <img src={file.preview} alt="" className="h-8 w-8 object-cover rounded" />
                  ) : (
                    <Paperclip className="h-5 w-5 text-gray-400" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-700">{file.file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.file.size)}</p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          {attachedFiles.length > 0 && `${attachedFiles.length} fichier(s) attaché(s)`}
        </p>
        <button
          onClick={handleSave}
          disabled={isUploading}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {isUploading ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
};

export default NewCommentEditor;